import { useEffect, useState, type FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Check, X } from 'lucide-react';
import { useI18n } from '@/i18n/use-i18n';
import '../AddAssetDialog/add-asset-dialog.css';

export interface EditAssetData {
  id: string;
  ticker: string;
  quantity: number;
  rating: number;
  averagePrice: number;
}

interface EditAssetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: EditAssetData | null;
  onSubmit: (data: { id: string; ticker: string; quantity: number; rating: number; averagePrice: number }) => void;
  isSubmitting?: boolean;
}

export function EditAssetDialog({
  open,
  onOpenChange,
  asset,
  onSubmit,
  isSubmitting = false,
}: EditAssetDialogProps) {
  const { strings } = useI18n();
  const t = strings.targetPortfolio.editAsset;

  const [ticker, setTicker] = useState('');
  const [quantity, setQuantity] = useState<number>(0);
  const [rating, setRating] = useState<number>(0);
  const [averagePrice, setAveragePrice] = useState<number>(0);

  useEffect(() => {
    if (asset && open) {
      setTicker(asset.ticker);
      setQuantity(asset.quantity);
      setRating(asset.rating);
      setAveragePrice(asset.averagePrice);
    }
  }, [asset, open]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!asset || !ticker.trim()) return;
    if (quantity > 0 && averagePrice <= 0) return;

    onSubmit({
      id: asset.id,
      ticker: ticker.toUpperCase().trim(),
      quantity,
      rating: Math.min(10, Math.max(0, Math.round(rating))),
      averagePrice,
    });
  };

  const isValid = ticker.trim() && (quantity === 0 || averagePrice > 0);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="add-asset-dialog__overlay" />
        <Dialog.Content className="add-asset-dialog">
          <div className="add-asset-dialog__header">
            <div>
              <Dialog.Title className="add-asset-dialog__title">
                {t.dialogTitle}
              </Dialog.Title>
              <Dialog.Description className="add-asset-dialog__description">
                {t.dialogDescription}
              </Dialog.Description>
            </div>
            <Dialog.Close className="add-asset-dialog__close" aria-label="Fechar">
              <X size={18} />
            </Dialog.Close>
          </div>

          <form className="add-asset-dialog__form" onSubmit={handleSubmit}>
            <div className="add-asset-dialog__row">
              <div className="add-asset-dialog__field">
                <label htmlFor="edit-asset-ticker">{t.tickerLabel}</label>
                <input
                  id="edit-asset-ticker"
                  type="text"
                  value={ticker}
                  onChange={(e) => setTicker(e.target.value.toUpperCase().replace(/\s+/g, ''))}
                  required
                />
              </div>
              <div className="add-asset-dialog__field">
                <label htmlFor="edit-asset-quantity">{t.quantityLabel}</label>
                <input
                  id="edit-asset-quantity"
                  type="number"
                  min={0}
                  step={1}
                  value={quantity || ''}
                  onChange={(e) => setQuantity(Math.max(0, Number(e.target.value)))}
                />
              </div>
            </div>

            <div className="add-asset-dialog__field">
              <label htmlFor="edit-asset-avg-price">{t.averagePriceLabel}</label>
              <input
                id="edit-asset-avg-price"
                type="number"
                min={0}
                step={0.01}
                placeholder={t.averagePricePlaceholder}
                value={averagePrice || ''}
                onChange={(e) => setAveragePrice(Math.max(0, Number(e.target.value)))}
              />
            </div>

            <div className="add-asset-dialog__field">
              <label htmlFor="edit-asset-rating">
                {t.ratingLabel}
              </label>
              <input
                id="edit-asset-rating"
                type="number"
                min={0}
                max={10}
                step={1}
                value={rating}
                onChange={(e) => setRating(Math.min(10, Math.max(0, Number(e.target.value))))}
              />
              <span className="add-asset-dialog__hint">{t.ratingHint}</span>
            </div>

            <div className="add-asset-dialog__actions">
              <Dialog.Close asChild>
                <button type="button" className="add-asset-dialog__cancel">
                  {t.cancel}
                </button>
              </Dialog.Close>
              <button
                type="submit"
                className="add-asset-dialog__submit"
                disabled={!isValid || isSubmitting}
              >
                <Check size={16} />
                {isSubmitting ? t.submitting : t.submit}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
